import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { startPoll } from "@/lib/poll";
import { useAuth } from "@/context/AuthContext";
import { timeAgo, formatApiError, limitWords } from "@/lib/format";
import { useProfileViewer } from "@/components/Layout";
import { SpeakModal } from "@/components/SpeakModal";
import { SpeakCall } from "@/components/SpeakCall";
import { toast } from "sonner";

function ChatView({ conversationId, onBack }) {
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [speakModal, setSpeakModal] = useState(false);
  const [speakCall, setSpeakCall] = useState(false);
  const bottomRef = useRef(null);
  const { viewProfile } = useProfileViewer();
  const isGuest = user?.is_guest;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/conversations/${conversationId}`);
      setConv(data);
    } catch (err) {
      console.error("Failed to load conversation", err);
    }
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => startPoll(load, 4000), [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [conv?.messages?.length]);

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/conversations/${conversationId}/messages`, { text });
      setText("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!conv) return <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>;

  const otherName = conv.other_user?.name || "unknown";

  return (
    <div className="fade-in flex flex-col" style={{ height: "70vh" }}>
      <div className="flex items-center justify-between gap-4 pb-6 mb-6" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="flex items-center gap-4 min-w-0">
          <button onClick={onBack} data-testid="dive-back-btn" className="text-lg px-3 py-2 -ml-2 opacity-60 hover:opacity-100" style={{ color: "var(--text-secondary)" }}>←</button>
          <button onClick={() => viewProfile(conv.other_user?.id)} data-testid="dive-other-user-btn" className="flex items-center gap-3 hover:opacity-100 opacity-90 transition-opacity min-w-0">
            <p className="font-head text-lg truncate" style={{ color: "var(--text-primary)" }}>{otherName}</p>
          </button>
        </div>
        {!isGuest && (
          <button onClick={() => setSpeakModal(true)} data-testid="speak-to-btn" className="btn shrink-0 px-4 py-2 text-xs tracking-widest">
            SPEAK
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col gap-5 pr-2">
        {conv.messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} data-testid={`dive-message-${m.id}`} className={`flex flex-col max-w-[75%] ${mine ? "self-end items-end" : "self-start items-start"}`}>
              <span className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>
                {m.kind === "inquiry" ? "ask" : m.kind === "response" ? "answer" : ""}
              </span>
              <div className="long-text-sm px-4 py-3" style={{ border: "1px solid var(--border)", background: mine ? "var(--bg-secondary)" : "transparent", color: "var(--text-primary)" }}>
                {m.text}
              </div>
              <span className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{timeAgo(m.created_at)}</span>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="pt-6 mt-4" style={{ borderTop: "1px solid var(--border)" }}>
        {conv.your_turn ? (
          <div className="flex gap-3">
            <input
              data-testid="dive-message-input"
              value={text}
              onChange={(e) => setText(limitWords(e.target.value, text, 1000))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
              placeholder="type a message…"
              className="flex-1 px-4 py-3"
              style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-primary)" }}
            />
            <button onClick={send} disabled={busy} data-testid="dive-send-btn" className="btn px-6 text-sm tracking-widest">SEND</button>
          </div>
        ) : (
          <p className="text-sm text-center" style={{ color: "var(--accent)" }} data-testid="dive-wait-turn">
            waiting for {otherName} to reply
          </p>
        )}
      </div>

      {speakModal && (
        <SpeakModal
          otherName={otherName}
          onCancel={() => setSpeakModal(false)}
          onStart={() => { setSpeakModal(false); setSpeakCall(true); }}
        />
      )}
      {speakCall && (
        <SpeakCall
          mode="caller"
          conversationId={conversationId}
          otherName={otherName}
          onClose={() => setSpeakCall(false)}
        />
      )}
    </div>
  );
}

function ChatSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openId, setOpenId] = useState(searchParams.get("c"));

  const closeChat = () => {
    setOpenId(null);
    if (searchParams.get("c")) { searchParams.delete("c"); setSearchParams(searchParams, { replace: true }); }
  };

  useEffect(() => {
    if (openId) return;
    const fetchList = () => api.get("/conversations").then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
    fetchList();
    return startPoll(fetchList, 5000);
  }, [openId]);

  if (openId) return <ChatView conversationId={openId} onBack={closeChat} />;

  if (loading) return <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>;
  if (items.length === 0) return <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="dive-empty">No conversations yet.</p>;

  return (
    <ul>
      {items.map((c, i) => (
        <li
          key={c.id}
          data-testid={`dive-conv-${c.id}`}
          className="fade-in-up py-5 px-4 cursor-pointer transition-colors duration-200 flex items-center gap-4"
          style={{ borderBottom: "1px solid var(--border)", animationDelay: `${i * 40}ms` }}
          onClick={() => setOpenId(c.id)}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-secondary)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <div className="flex-1 min-w-0">
            <p className="font-head text-lg" style={{ color: "var(--text-primary)" }}>{c.other_user?.name || "unknown"}</p>
            <p className="text-sm truncate" style={{ color: "var(--text-muted)" }}>{c.last_message}</p>
          </div>
          {c.your_turn && (
            <span className="text-xs tracking-widest shrink-0" style={{ color: "var(--accent)" }} data-testid={`dive-yourturn-${c.id}`}>
              YOUR TURN
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

function SpeakInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    const fetchList = () => api.get("/speak/incoming").then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
    fetchList();
    return startPoll(fetchList, 3000);
  }, []);

  if (loading) return <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>;

  return (
    <div>
      {items.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="speak-inbox-empty">No live requests.</p>
      ) : (
        <ul data-testid="speak-inbox">
          {items.map((s, i) => (
            <li
              key={s.id}
              data-testid={`speak-request-${s.id}`}
              className="fade-in-up py-5 px-4 flex items-center gap-4"
              style={{ borderBottom: "1px solid var(--border)", animationDelay: `${i * 40}ms` }}
            >
              <div className="flex-1 min-w-0">
                <p className="font-head text-lg" style={{ color: "var(--text-primary)" }}>{s.caller_name || "unknown"}</p>
                <p className="text-xs tracking-widest" style={{ color: "var(--accent)" }}>wants to speak · {timeAgo(s.created_at)}</p>
              </div>
              <button
                onClick={() => setAccepted({ id: s.id, name: s.caller_name })}
                data-testid={`speak-accept-${s.id}`}
                className="btn shrink-0 px-5 py-2 text-xs tracking-widest"
              >
                ACCEPT
              </button>
            </li>
          ))}
        </ul>
      )}

      {accepted && (
        <SpeakCall
          mode="callee"
          sessionId={accepted.id}
          otherName={accepted.name}
          onClose={() => setAccepted(null)}
        />
      )}
    </div>
  );
}

export default function DivePage() {
  const [tab, setTab] = useState("chat");
  const [incoming, setIncoming] = useState(0);

  useEffect(() => {
    const fetchCount = () => api.get("/speak/incoming").then(({ data }) => setIncoming(data.length)).catch(() => {});
    fetchCount();
    return startPoll(fetchCount, 4000);
  }, []);

  const subtabCls = (active) =>
    `text-sm tracking-widest transition-opacity duration-200 ${active ? "opacity-100" : "opacity-50 hover:opacity-80"}`;
  const subtabStyle = (active) => ({
    color: active ? "var(--accent)" : "var(--text-secondary)",
    borderBottom: active ? "1px solid var(--accent)" : "1px solid transparent",
    paddingBottom: "4px",
  });

  return (
    <div>
      <h1 className="font-head font-light text-4xl tracking-tighter mb-6" style={{ color: "var(--text-primary)" }}>Chat</h1>

      <div className="flex gap-8 mb-8" data-testid="chat-subtabs">
        <button data-testid="subtab-chat" onClick={() => setTab("chat")} className={subtabCls(tab === "chat")} style={subtabStyle(tab === "chat")}>
          CHAT
        </button>
        <button data-testid="subtab-speak" onClick={() => setTab("speak")} className={subtabCls(tab === "speak")} style={subtabStyle(tab === "speak")}>
          SPEAK{incoming > 0 ? ` · ${incoming}` : ""}
        </button>
      </div>

      {tab === "chat" ? <ChatSection /> : <SpeakInbox />}
    </div>
  );
}
